import './bootstrap';

// Polyfill Object.hasOwn for Safari < 15.4
if (!Object.hasOwn) {
    Object.hasOwn = (obj, prop) => Object.prototype.hasOwnProperty.call(obj, prop);
}

import { createSSRApp, h } from 'vue';
import { createInertiaApp, router } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createI18n } from 'vue-i18n';
import VueCountdown from '@chenfengyuan/vue-countdown';
import Toast from 'vue-toastification';
 import 'vue-toastification/dist/index.css';
 import VueCookies from 'vue3-cookies';
 import { InertiaProgress } from '@inertiajs/progress'; // Import progress indicator
 import { route } from 'ziggy-js';
 import currencyService from './services/CurrencyService';
 import { syncOpenAiPixelUser } from './services/openaiPixel';
 import * as Sentry from '@sentry/vue';

 /* import the fontawesome core */
 import { config, library } from '@fortawesome/fontawesome-svg-core';
 // FontAwesome adds its CSS from JavaScript by default, so server-rendered icons show at
 // full size until the app loads and then shrink: the layout shift on /register. Ship
 // the CSS with the page's own stylesheet instead.
 import '@fortawesome/fontawesome-svg-core/styles.css';
 config.autoAddCss = false;
 /* import font awesome icon component */
 import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
 /* import specific icons if needed globally - prefer importing in components */
 // import { faUserSecret } from '@fortawesome/free-solid-svg-icons'
 /* add icons to the library */
 // library.add(faUserSecret)

// Import Layouts
import AppLayout from './Layouts/AppLayout.vue';
import SupportPanelLayout from './Layouts/SupportPanelLayout.vue';

// Recover from stale asset references after a deploy. When a dynamically imported
// page chunk (or its CSS) can no longer be fetched because the build hash changed,
// Vite dispatches `vite:preloadError`. Reload once to pull the fresh build manifest.
// The time-guard prevents a reload loop if the asset is genuinely gone.
if (typeof window !== 'undefined') {
    let reloadPending = false;
    window.addEventListener('vite:preloadError', (event) => {
        const KEY = 'vite:preloadError:lastReload';
        const now = Date.now();
        const last = Number(window.sessionStorage.getItem(KEY) || 0);
        if (reloadPending || now - last > 10000) {
            // Swallowing the error makes the failed import resolve `undefined`;
            // resolve() below parks those navigations until the reload lands.
            event.preventDefault();
        }
        if (!reloadPending && now - last > 10000) {
            reloadPending = true;
            window.sessionStorage.setItem(KEY, String(now));
            window.location.reload();
        }
    });

    // Re-report a page view to Meta, Reddit and ChatGPT Ads on SPA navigations. The first
    // navigate event is the initial load, whose PageView the pixel inits already
    // fire, so skip it. GA4 is deliberately left to Enhanced Measurement's history
    // tracking to avoid double-counting page_views.
    let firstNavigate = true;
    router.on('navigate', (event) => {
        // Before the page view below, so it already carries a fresh login.
        syncOpenAiPixelUser(event.detail.page.props);
        if (firstNavigate) {
            firstNavigate = false;
            return;
        }
        if (typeof fbq === 'function') {
            fbq('track', 'PageView');
        }
        if (typeof rdt === 'function') {
            rdt('track', 'PageVisit');
        }
        if (typeof oaiq === 'function') {
            oaiq('measure', 'page_viewed', { type: 'contents' });
        }
    });
}

createInertiaApp({
    // Page titles already carry the brand, and ssr.js renders them as they are. Appending
    // the first page's <title> here doubled it ("X - X"), and every page reached by a link
    // kept the first page's title on the end.
    title: (title) => title,
    // Dynamically load page components and apply layout based on path
    resolve: (name) => {
        // Use resolvePageComponent for cleaner dynamic imports.
        // Chain (rather than fork) the layout logic so a failed import doesn't
        // surface as a second unhandled rejection from a floating promise.
        return resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')).then((module) => {
            // A swallowed vite:preloadError (stale build after a deploy) resolves
            // the import as `undefined` while location.reload() is already in
            // flight. Park the navigation so nothing dereferences the module.
            if (!module) {
                return new Promise(() => {});
            }
            // Prioritize checking for the Maintenance page
            if (name === 'Maintenance') {
                module.default.layout = null; // Ensure no layout is applied
            }
            // Check if layout is explicitly defined on the page component itself (could be null)
            else if (module.default.layout !== undefined) {
                // Respect the explicitly defined layout (or lack thereof if null)
                // No action needed here, layout is already set by the page component
            }
            // Apply default layout logic ONLY if not Maintenance and layout is undefined
            else {
                if (name.startsWith('SupportPanel/') || name.startsWith('Admin/')) {
                    module.default.layout = SupportPanelLayout;
                } else {
                    module.default.layout = AppLayout;
                }
            }
            return module;
        });
    },
    setup({ el, App, props, plugin }) {
        const vueApp = createSSRApp({ render: () => h(App, props) });

        // Initialize Sentry (client-side only)
        if (typeof window !== 'undefined') {
            Sentry.init({
                app: vueApp,
                dsn: import.meta.env.VITE_SENTRY_DSN,
                integrations: [
                    Sentry.browserTracingIntegration(),
                    Sentry.replayIntegration(),
                ],
                tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '1.0'),
                replaysSessionSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0.1'),
                replaysOnErrorSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE || '1.0'),
                // Third-party noise that isn't our code — don't ingest it.
                ignoreErrors: [
                    // Injected crypto-wallet browser extensions (MetaMask, etc.)
                    /Failed to connect to MetaMask/i,
                    /MetaMask/i,
                    // A wallet extension fighting over the injected window.ethereum global
                    /Cannot redefine property: ethereum/i,
                    // Telegram Mini App SDK fired when the site is opened outside Telegram
                    /Error invoking postEvent/i,
                    /WebAppMethodUnsupported/i,
                    // Injected third-party / extension code, not ours
                    /func sseError not found/i,
                    // Browser-compat probes from third-party scripts on old/uncommon engines
                    /requestIdleCallback/i,
                    /getEntriesByType/i,
                    // Cross-origin iframe/embed noise we can't act on
                    /Origin not allowed/i,
                    // Stale hashed assets requested after a deploy. The
                    // vite:preloadError handler above recovers via reload; the
                    // stragglers are CDN/tab-cached HTML we can't act on.
                    /Failed to fetch dynamically imported module/i,
                    /error loading dynamically imported module/i,
                    /Importing a module script failed/i,
                    /Unable to preload CSS/i,
                    // A leftover Crisp tag in the GTM container still calls
                    // $crisp, but the site switched to JuicyHelp. It throws from
                    // a Custom HTML tag GTM injects into the page itself, so the
                    // frame URL is juicysms.com and denyUrls can't catch it.
                    /\$crisp\b/,
                ],
                denyUrls: [
                    // Errors thrown from browser extensions, not from our bundle
                    /^chrome-extension:\/\//,
                    /^moz-extension:\/\//,
                    /^safari-(web-)?extension:\/\//,
                    // Third-party ad/analytics scripts
                    /doubleclick\.net/,
                    /googlesyndication\.com/,
                    /googletagmanager\.com/,
                    // Stape first-party GTM loader — same third-party code, our subdomain.
                    // Host-anchored: must never match app pages on juicysms.com itself.
                    /\/\/juicyt\.juicysms\.com\//,
                    /redditstatic\.com/,
                    /connect\.facebook\.net/,
                    /crazyegg\.com/,
                    /cookie-script\.com/,
                ],
                // Defence-in-depth: strip credentials from URLs/breadcrumbs before sending,
                // so a leaked query-string secret can never reach Sentry.
                beforeSend(event) {
                    const SECRET_PARAMS = /([?&](?:client_secret|secret|token|api_key|apikey|access_token|authorization|password)=)[^&#]*/gi;
                    const scrub = (url) =>
                        typeof url === 'string' ? url.replace(SECRET_PARAMS, '$1[Filtered]') : url;
                    if (event.request?.url) {
                        event.request.url = scrub(event.request.url);
                    }
                    if (Array.isArray(event.breadcrumbs)) {
                        event.breadcrumbs.forEach((bc) => {
                            if (bc?.data?.url) bc.data.url = scrub(bc.data.url);
                        });
                    }
                    return event;
                },
            });
        }

        // Use Inertia plugin
        vueApp.use(plugin);

        // --- i18n Setup ---
        const initialProps = props.initialPage.props;
        const currentLocale = initialProps.locale || 'en';
        const fallbackLocale = 'en'; // Or get from props if shared
        const initialTranslations = initialProps.translations || {};

        // Structure messages for vue-i18n
        const messages = {
            [currentLocale]: initialTranslations
        };
        // Add fallback locale messages if different and available
        // Assuming fallback translations might be passed separately or loaded differently if needed
        // if (currentLocale !== fallbackLocale && initialProps.fallbackTranslations) {
        //     messages[fallbackLocale] = initialProps.fallbackTranslations;
        // }

        const i18n = createI18n({
            legacy: false, // Use Composition API mode
            globalInjection: true, // Inject $t, etc. globally
            locale: currentLocale,
            fallbackLocale: fallbackLocale,
            messages: messages, // Re-added messages object
            // Silence warnings about missing keys, let fallback handle it
            missingWarn: false,
            fallbackWarn: false,
            // Locale messages intentionally contain static HTML in trusted marketing copy;
            // disable the intlify HTML-in-message warning (kept in sync with ssr.js).
            warnHtmlMessage: false,
        });
        vueApp.use(i18n);
        // --- End i18n Setup ---

        // --- Watchers removed, will be handled in AppLayout ---


        /**
         * Register global components
         */
        vueApp.component(VueCountdown.name, VueCountdown);
        vueApp.component('font-awesome-icon', FontAwesomeIcon); // Register FA component globally
        // Using ES module import instead of require
        // Note: Global registration might be less common with Inertia pages,
        // consider importing components directly in pages where needed.
        // If you still need global components:
        // import ExampleComponent from './components/ExampleComponent.vue';
        // vueApp.component('example-component', ExampleComponent);
        // import ServiceDetails from './components/ServiceDetails.vue';
        // vueApp.component('service-details', ServiceDetails);
        // import CountryServices from './components/CountryServices.vue';
        // vueApp.component('country-services', CountryServices);
        // import StickySidebar from './components/StickySidebar.vue';
        // vueApp.component('sticky-sidebar', StickySidebar);
        // import TrustpilotBar from './components/TrustpilotBar.vue';
        // vueApp.component('trustpilot-bar', TrustpilotBar);

        /**
         * Auto-register Vue components (Consider if still needed with page components)
         */
        // const files = import.meta.glob('./components/**/*.vue', { eager: true });
        // Object.entries(files).forEach(([path, component]) => {
        //     const componentName = path.split('/').pop().replace(/\.\w+$/, '');
        //     vueApp.component(componentName, component.default || component);
        // });

        /**
         * Use other plugins
         */
        vueApp.use(Toast, {
            /* Toast options */
        });
        vueApp.use(VueCookies);
        
        // Make route helper available globally
        vueApp.config.globalProperties.route = route;

        // Initialize currency service with data from Inertia props
        if (props.initialPage.props.currency) {
            currencyService.init(props.initialPage.props.currency); // Revert to initial props
        }

        // Mount the app to the DOM element provided by Inertia
        vueApp.mount(el);

        // Make app accessible globally if needed (less common with Inertia)
        // window.app = vueApp;

        return vueApp; // Return the app instance
    },
    progress: { // Add progress indicator configuration
        color: '#4B5563',
    },
});

// Initialize the progress indicator (client-side only)
if (typeof window !== 'undefined') {
    InertiaProgress.init();
}
